import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Zap, Crown, Building2, Users, ArrowRight, Shield, Activity, Truck, Factory, Warehouse, Book } from 'lucide-react';
interface LoginProps {
  onLogin: (role: 'buyer' | 'admin' | 'superadmin' | 'logistics' | 'factory' | 'warehouse') => void;
  onNavigate?: (path: string) => void;
}
export function Login({
  onLogin,
  onNavigate
}: LoginProps) {
  const [selectedRole, setSelectedRole] = useState<'buyer' | 'admin' | 'superadmin' | 'logistics' | 'factory' | 'warehouse' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const roles = [{
    id: 'buyer' as const,
    name: 'Buyer Portal',
    subtitle: 'Koperasi Maju Jaya',
    description: 'Akses portal procurement untuk koperasi',
    icon: Users,
    color: 'blue',
    gradient: 'from-blue-500 to-blue-600',
    bgGradient: 'from-blue-50 to-blue-100',
    demoCredentials: {
      email: 'buyer@majujaya.coop',
      password: 'demo123',
      name: 'Budi Santoso',
      position: 'Ketua Koperasi'
    }
  }, {
    id: 'admin' as const,
    name: 'PTX Admin',
    subtitle: 'PT Perdagangan Terpadu X',
    description: 'Dashboard operasional agregator',
    icon: Zap,
    color: 'slate',
    gradient: 'from-slate-700 to-slate-900',
    bgGradient: 'from-slate-50 to-slate-100',
    demoCredentials: {
      email: 'admin@ptx.co.id',
      password: 'demo123',
      name: 'Admin Operasional',
      position: 'Super Admin PTX'
    }
  }, {
    id: 'superadmin' as const,
    name: 'Super Admin',
    subtitle: 'Platform Owner',
    description: 'Monitor platform & revenue analytics',
    icon: Crown,
    color: 'violet',
    gradient: 'from-violet-600 to-violet-800',
    bgGradient: 'from-violet-50 to-violet-100',
    demoCredentials: {
      email: 'owner@kdmp.com',
      password: 'demo123',
      name: 'Platform Owner',
      position: 'Super Admin'
    }
  }, {
    id: 'logistics' as const,
    name: 'Logistics Partner',
    subtitle: 'JNE Trucking',
    description: 'Monitor pengiriman dan performa kurir',
    icon: Truck,
    color: 'green',
    gradient: 'from-green-600 to-green-700',
    bgGradient: 'from-green-50 to-green-100',
    demoCredentials: {
      email: 'partner@jne.co.id',
      password: 'demo123',
      name: 'JNE Operations',
      position: 'Logistics Partner'
    }
  }, {
    id: 'factory' as const,
    name: 'Factory Portal',
    subtitle: 'PT Indofood',
    description: 'Monitor PO dan produksi pabrik',
    icon: Factory,
    color: 'orange',
    gradient: 'from-orange-600 to-orange-700',
    bgGradient: 'from-orange-50 to-orange-100',
    demoCredentials: {
      email: 'po@indofood.co.id',
      password: 'demo123',
      name: 'Factory Manager',
      position: 'Production Head'
    }
  }, {
    id: 'warehouse' as const,
    name: 'Warehouse Portal',
    subtitle: 'Gudang Transit Bogor',
    description: 'Kelola inbound & outbound gudang',
    icon: Warehouse,
    color: 'cyan',
    gradient: 'from-cyan-600 to-cyan-700',
    bgGradient: 'from-cyan-50 to-cyan-100',
    demoCredentials: {
      email: 'bogor@ptx-warehouse.co.id',
      password: 'demo123',
      name: 'Agus Setiawan',
      position: 'Warehouse Manager'
    }
  }];
  const handleRoleSelect = (role: (typeof roles)[0]) => {
    setSelectedRole(role.id);
    setEmail(role.demoCredentials.email);
    setPassword(role.demoCredentials.password);
  };
  const handleLogin = () => {
    if (selectedRole) {
      onLogin(selectedRole);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-violet-50 flex items-center justify-center p-6" data-id="element-2275">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" data-id="element-2276">
        <div className="absolute inset-0" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} data-id="element-2277"></div>
      </div>

      <div className="w-full max-w-7xl relative z-10" data-id="element-2278">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-2279">
          <div className="flex items-center justify-center gap-3 mb-4" data-id="element-2280">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-violet-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-blue-500/50" data-id="element-2281">
              <Building2 className="w-8 h-8 text-white" data-id="element-2282" />
            </div>
          </div>
          <h1 className="text-4xl font-extrabold text-slate-900 mb-2" data-id="element-2283">
            KDMP B2B Platform
          </h1>
          <p className="text-lg text-slate-600 mb-4" data-id="element-2284">
            Procurement & Supply Chain Management System
          </p>

          {/* Documentation Button */}
          {onNavigate && <Button variant="outline" size="sm" icon={<Book className="w-4 h-4" data-id="element-2286" />} onClick={() => onNavigate('/documentation')} className="mx-auto" data-id="element-2285">
              Lihat Dokumentasi Sistem
            </Button>}
        </div>

        {!selectedRole /* Role Selection */ ? <div data-id="element-2287">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" data-id="element-2288">
              Pilih Portal untuk Demo
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2289">
              {roles.map(role => {
            const Icon = role.icon;
            return <button key={role.id} onClick={() => handleRoleSelect(role)} className="group relative" data-id="element-2290">
                    <Card className={`p-8 bg-gradient-to-br ${role.bgGradient} border-2 border-${role.color}-200 hover:border-${role.color}-400 transition-all duration-300 hover:shadow-2xl hover:scale-105 cursor-pointer`} data-id="element-2291">
                      <div className="text-center" data-id="element-2292">
                        <div className={`w-20 h-20 bg-gradient-to-br ${role.gradient} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:shadow-2xl transition-shadow`} data-id="element-2293">
                          <Icon className="w-10 h-10 text-white" data-id="element-2294" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-2295">
                          {role.name}
                        </h3>
                        <p className={`text-sm font-medium text-${role.color}-700 mb-3`} data-id="element-2296">
                          {role.subtitle}
                        </p>
                        <p className="text-sm text-slate-600 mb-6" data-id="element-2297">
                          {role.description}
                        </p>

                        <div className={`p-4 bg-white rounded-lg border border-${role.color}-200 text-left`} data-id="element-2298">
                          <p className="text-xs text-slate-500 mb-2" data-id="element-2299">
                            Demo Credentials:
                          </p>
                          <p className="text-xs font-mono text-slate-700 mb-1" data-id="element-2300">
                            {role.demoCredentials.email}
                          </p>
                          <p className="text-xs font-mono text-slate-700" data-id="element-2301">
                            Password: {role.demoCredentials.password}
                          </p>
                        </div>

                        <div className={`mt-6 flex items-center justify-center gap-2 text-${role.color}-600 font-bold group-hover:gap-4 transition-all`} data-id="element-2302">
                          <span data-id="element-2303">Login sebagai {role.name}</span>
                          <ArrowRight className="w-5 h-5" data-id="element-2304" />
                        </div>
                      </div>
                    </Card>
                  </button>;
          })}
            </div>

            {/* Info Banner */}
            <Card className="mt-8 p-6 bg-blue-50 border-blue-200" data-id="element-2305">
              <div className="flex items-start gap-4" data-id="element-2306">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-2307">
                  <Shield className="w-6 h-6 text-white" data-id="element-2308" />
                </div>
                <div className="flex-1" data-id="element-2309">
                  <h3 className="font-bold text-blue-900 mb-2" data-id="element-2310">
                    Demo Mode - 6 Portal Terintegrasi
                  </h3>
                  <p className="text-sm text-blue-800 leading-relaxed" data-id="element-2311">
                    Semua data di 6 portal ini terhubung dan konsisten. Order
                    dari Buyer → PTX Admin → Factory → Warehouse → Logistics →
                    Delivery. Revenue tercatat di Super Admin. Explore complete
                    supply chain flow!
                  </p>
                </div>
              </div>
            </Card>
          </div> /* Login Form */ : <Card className="max-w-md mx-auto p-8 shadow-2xl" data-id="element-2312">
            <button onClick={() => setSelectedRole(null)} className="text-sm text-slate-600 hover:text-slate-900 mb-6 flex items-center gap-2" data-id="element-2313">
              ← Kembali ke pilihan portal
            </button>

            <div className="text-center mb-8" data-id="element-2314">
              {(() => {
            const role = roles.find(r => r.id === selectedRole)!;
            const Icon = role.icon;
            return <>
                    <div className={`w-20 h-20 bg-gradient-to-br ${role.gradient} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl`} data-id="element-2315">
                      <Icon className="w-10 h-10 text-white" data-id="element-2316" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-2317">
                      {role.name}
                    </h2>
                    <p className="text-sm text-slate-600" data-id="element-2318">{role.subtitle}</p>
                  </>;
          })()}
            </div>

            <div className="space-y-4" data-id="element-2319">
              <div data-id="element-2320">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-2321">
                  Email
                </label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="email@example.com" data-id="element-2322" />
              </div>

              <div data-id="element-2323">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-2324">
                  Password
                </label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="••••••••" data-id="element-2325" />
              </div>

              <Button variant="primary" size="lg" fullWidth onClick={handleLogin} icon={<ArrowRight className="w-5 h-5" data-id="element-2327" />} className="mt-6" data-id="element-2326">
                Login ke {roles.find(r => r.id === selectedRole)?.name}
              </Button>
            </div>

            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-2328">
              <p className="text-xs text-slate-600 text-center" data-id="element-2329">
                <span className="font-bold" data-id="element-2330">Demo Mode:</span> Gunakan
                credentials yang sudah terisi otomatis
              </p>
            </div>
          </Card>}

        {/* Footer */}
        <div className="mt-12 text-center" data-id="element-2331">
          <div className="flex items-center justify-center gap-2 text-sm text-slate-600 mb-2" data-id="element-2332">
            <Activity className="w-4 h-4 text-green-600 animate-pulse" data-id="element-2333" />
            <span data-id="element-2334">
              Platform Status:{' '}
              <span className="font-bold text-green-600" data-id="element-2335">Operational</span>
            </span>
          </div>
          <p className="text-xs text-slate-500" data-id="element-2336">
            KDMP B2B Platform - Complete Supply Chain Management © 2026
          </p>
        </div>
      </div>
    </div>;
}